import { io, Socket } from 'socket.io-client';

import { SOCKETIO_PATH, WS_GATEWAY_URL } from '@frontend/envConfig';
import { SocketWakeSignals } from '@frontend/services/socketWakeSignals';

type ManagerReservedEventNames =
  | 'reconnect'
  | 'reconnect_attempt'
  | 'reconnect_error'
  | 'reconnect_failed'
  | 'ping';

class SocketService {
  private socket: Socket | null = null;

  /**
   * Set when the app itself asked to disconnect (provider unmount, instance
   * switch). Wake signals must not resurrect a socket nobody wants — the
   * listeners outlive any one provider because this service is a singleton.
   */
  private intentionallyDisconnected = false;

  /**
   * The way back after a sleep. Without this the socket can sit in backoff
   * against a network that is already up, and — worse — report `connected`
   * while its transport is dead, which engine.io takes 45s to notice.
   */
  private wake = new SocketWakeSignals({
    cycle: () => {
      if (this.intentionallyDisconnected) return;
      this.socket?.disconnect();
      this.socket?.connect();
    },
    isConnected: () => this.socket?.connected ?? false,
    revive: () => {
      if (this.intentionallyDisconnected) return;
      this.socket?.connect();
    },
  });

  /**
   * `getToken` is a provider, not a string, and that is load-bearing.
   *
   * socket.io captures a plain `auth` object once, so passing a token value
   * replays the SAME (eventually expired) token on every auto-reconnect. Past
   * the ~40-minute token lifetime every reconnect then fails auth and the
   * socket can never come back. Passing `auth` as a function makes socket.io
   * call it before EACH attempt, so a reconnect after a long sleep carries a
   * fresh token. Mirrors opal-desktop's desktop-socket.ts, which fixed exactly
   * this.
   */
  init(getToken: () => Promise<string>, instance_id: string, hostedRegion?: string) {
    // Clean up previous socket if it exists to prevent orphaned connections
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    // Region drives Global LB routing for the WebSocket. Browsers can't set
    // custom headers on a WebSocket upgrade and cookies are domain-scoped
    // (the multi-tab bug we fixed for HTTP), so we put the region in the URL
    // query string — per-tab via the URL itself. The LB matches
    // `queryParameterMatches.exactMatch=eu` on the upgrade request and routes
    // the connection to the EU backend service. Mirrors chat-frontend's
    // useThreadSocket.ts; the same value rides in `auth` so ws-gateway can
    // reject mismatches once the connection is up.
    const region = (hostedRegion || 'US').toLowerCase();

    this.socket = io(WS_GATEWAY_URL, {
      path: SOCKETIO_PATH,
      transports: ['websocket'],
      withCredentials: true,
      autoConnect: false,
      query: {
        region,
      },
      auth: (cb: (data: Record<string, unknown>) => void) => {
        const payload = (token: string) => ({
          opti_id_access_token: token,
          instance_id,
          hosted_region: hostedRegion || 'US',
        });
        // Hand back an empty token rather than never calling `cb`: socket.io
        // waits on this callback, so swallowing a renewal failure here would
        // stall the connection attempt forever instead of failing it and
        // retrying.
        void getToken().then(
          token => cb(payload(token)),
          () => cb(payload(''))
        );
      },

      // Reconnection configuration. Never stop trying: the old cap of 10 gave
      // up after roughly 42s, which is shorter than a laptop sleep, and nothing
      // reconnects afterwards — the provider effect only re-runs on an instance
      // or region change. The socket stayed dead until a reload, and because
      // React Query falls back to polling while `connected` is false, every
      // affected tab then polled every 3-5s indefinitely.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5,
    });

    // Feed the wake bookkeeping from the socket itself rather than from the
    // provider: it needs to know how long we were down to judge whether a
    // `connected` flag can still be trusted, and that must hold even if a
    // provider unmounts mid-outage.
    this.socket.on('connect', () => this.wake.noteConnected());
    this.socket.on('disconnect', () => this.wake.noteDisconnected());

    return this.socket;
  }

  connect() {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot connect.');
      return;
    }

    console.warn('Connecting socket to server...');
    this.intentionallyDisconnected = false;
    this.wake.bind();
    this.socket.connect();
  }

  disconnect() {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot disconnect.');
      return;
    }

    console.warn('Disconnecting socket from server...');
    this.intentionallyDisconnected = true;
    this.socket.disconnect();
  }

  emit(event: string, data?: unknown) {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot emit event:', event);
      return;
    }
    this.socket.emit(event, data);
  }

  on<T>(event: string, callback: (data: T) => void) {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot subscribe to event:', event);
      return;
    }
    this.socket.on(event, callback);
  }

  off<T>(event: string, callback?: (data: T) => void) {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot unsubscribe from event:', event);
      return;
    }
    this.socket.off(event, callback);
  }

  onManager(event: ManagerReservedEventNames, callback: (...args: unknown[]) => void): void {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot subscribe to manager event:', event);
      return;
    }

    this.socket.io.on(event, callback);
  }

  offManager(event: ManagerReservedEventNames, callback?: (...args: unknown[]) => void): void {
    if (!this.socket) {
      console.warn('Socket not initialized. Cannot unsubscribe from manager event:', event);
      return;
    }

    this.socket.io.off(event, callback);
  }

  isConnected() {
    return this.socket?.connected ?? false;
  }

  getSocket() {
    return this.socket;
  }

  /** Stop the wake watchdog. Tests need this so a 1s interval doesn't leak. */
  disposeWakeSignals() {
    this.wake.dispose();
  }
}

export const socketService = new SocketService();
